import React, { useState,useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { formatDate } from '@utils/dates/date.utils';
import { MM_SLASH_DD_SLASH_YYYY } from '@utils/dates/dateFormats.utils';
import { IProgramEligibility } from '../types/Applications.interface';
import useApiHandling from './useApiHandling';
import {
    APPLICANT_ELIGIBILITY_TYPE,
    aptcAmntValidate,
    householdStateSubsidyAmountValidate,
    eligibilityStartDateValidation,
    bhpEligibilityValidate,
    YesNo,
    PROGRAM_ELIGIBILITY_MODAL_PHASE,
    OverrideProgramEligibilityModalPhaseType,
    parseCurrencyToNumber,
    formatCurrencyValue,
    applyNotEligibleDefaults,
    restoreOriginalEligibility,
    IS_ELIGIBLE,
    PREMIUM_ASSISTANCE_SUBSIDY_STATES,
} from '../utils';


interface IUseOverrideEligibilityProps {
    data: IProgramEligibility;
    stateCode: string;
    application: {
        coverageYear: number;
        caseNumber: string;
    };
    closeModal: (modalName: string) => void;
}

interface IErrorState {
    aptcZeroAmntErrorFlag: boolean;
    aptcMaxAmntErrorFlag: boolean;
    isNan: boolean;
    differentYear: boolean;
    invalidStartDate: boolean;
    hasHouseHoldStateSubsidyAmountInvalidError: boolean;
    hasHouseHoldStateSubsidyAmountNotEligibleError: boolean;
    hasBhpEligibilityError: boolean;
}

const INITIAL_ERROR_STATE: IErrorState = {
    aptcZeroAmntErrorFlag: false,
    aptcMaxAmntErrorFlag: false,
    isNan: false,
    differentYear: false,
    invalidStartDate: false,
    hasHouseHoldStateSubsidyAmountInvalidError: false,
    hasHouseHoldStateSubsidyAmountNotEligibleError: false,
    hasBhpEligibilityError: false,
};

export const useOverrideEligibility = ({
    data,
    stateCode,
    application,
    closeModal,
}: IUseOverrideEligibilityProps) => {
    const isState = useSelector((state: any) => state?.common?.environment.isState);
    const hasPremiumAssistanceSubsidyFields = PREMIUM_ASSISTANCE_SUBSIDY_STATES.includes(stateCode);
    const { submitOverrideProgElig } = useApiHandling();
    const [overrideEligibilityData, setOverrideEligibilityData] = useState<IProgramEligibility>({
        ...data,
        eligibilityStartDate: new Date(data.eligibilityStartDate),
    });

    const [householdStateSubsidyAmountValue, setHouseholdStateSubsidyAmountValue] = useState<string>(() => {
        const amount = data.householdStateSubsidyAmount ?? 0;
        return `$${amount.toFixed(2)}`;
    });

    const [maximumAPTCValue, setMaximumAPTCValue] = useState<string>(() => {
        const amount = data.maximumAPTC ?? 0;
        return `$${amount.toFixed(2)}`;
    });

    const [isHouseholdStateSubsidyAmountDirty, setIsHouseholdStateSubsidyAmountDirty] = useState(false);
    const [isMaximumAPTCDirty, setIsMaximumAPTCDirty] = useState(false);
    const [error, setError] = useState<IErrorState>(INITIAL_ERROR_STATE);
    const [confirmationText, setConfirmationText] = useState('');
    const [overrideProgramEligibilityModalPhase, setOverrideProgramEligibilityModalPhase] =
        useState<OverrideProgramEligibilityModalPhaseType>('override');

    const isModalPhaseOverride = overrideProgramEligibilityModalPhase === PROGRAM_ELIGIBILITY_MODAL_PHASE;
    const isFinancialAssistanceFlag = overrideEligibilityData.financialAssistanceFlag === YesNo.Y;
    const isStateSubsidyOverrideEnabled = !!data.enableStateSubsidyOverride && isFinancialAssistanceFlag;
    const isBhpOverrideEnabled = !!data.enableBHPOverride && isFinancialAssistanceFlag;

    // Store original BHP types to determine allowed transitions
    const originalBhpTypes = useMemo(() => {
        return data.applicantEligibility.reduce((acc, applicant) => {
            acc[applicant.id] = applicant.bhpType;
            return acc;
        }, {} as Record<string | number, string>);
    }, [data.applicantEligibility]);

    // Memoize only the relevant eligibility values to avoid unnecessary re-renders
    const stateSubsidyEligibilityValues = useMemo(() => {
        if (hasPremiumAssistanceSubsidyFields) {
            return overrideEligibilityData.applicantEligibility.map(applicant => 
                `${applicant.newMexicoPremiumAssistance}|${applicant.nativeAmericanPremiumAssistance}|${applicant.stateOutOfPocketAssistance}`
            ).join(',') || '';
        } else {
            return overrideEligibilityData.applicantEligibility.map(applicant => 
                applicant.stateSubsidyEligibility
            ).join(',') || '';
        }

    }, [overrideEligibilityData.applicantEligibility, hasPremiumAssistanceSubsidyFields]);

    // Memoize BHP-related eligibility values to trigger validation on changes
    const bhpEligibilityValues = useMemo(() => {
        return overrideEligibilityData.applicantEligibility.map(applicant => 
            `${applicant.exchangeEligibility}|${applicant.aptcEligibility}|${applicant.bhpType}`
        ).join(',');
    }, [overrideEligibilityData.applicantEligibility]);


    useEffect(() => {
        if (!isHouseholdStateSubsidyAmountDirty) {
            const amount = overrideEligibilityData.householdStateSubsidyAmount ?? 0;
            setHouseholdStateSubsidyAmountValue(`$${amount.toFixed(2)}`);
        }
    }, [isHouseholdStateSubsidyAmountDirty, overrideEligibilityData.householdStateSubsidyAmount]);

    useEffect(() => {
        if (!isMaximumAPTCDirty) {
            const amount = Number(overrideEligibilityData.maximumAPTC) || 0;
            setMaximumAPTCValue(`$${amount.toFixed(2)}`);
        }
    }, [isMaximumAPTCDirty, overrideEligibilityData.maximumAPTC]);

    useEffect(() => {
        householdStateSubsidyAmountValidate(overrideEligibilityData, householdStateSubsidyAmountValue, stateCode, setError);
    }, [householdStateSubsidyAmountValue, stateSubsidyEligibilityValues, stateCode]);

    useEffect(() => {
        aptcAmntValidate(overrideEligibilityData.applicantEligibility, parseCurrencyToNumber(maximumAPTCValue), setError);
    }, [maximumAPTCValue, overrideEligibilityData.applicantEligibility]);

    useEffect(() => {
        // NJ hides the eligibility start date field in the UI, so its validation
        // must not contribute to the override-button disable gate.
        if (isState.NJ) {
            setError(prev =>
                prev.invalidStartDate || prev.differentYear
                    ? { ...prev, invalidStartDate: false, differentYear: false }
                    : prev
            );
            return;
        }
        eligibilityStartDateValidation(
            overrideEligibilityData.eligibilityStartDate,
            application.coverageYear,
            setError,
        );
    }, [overrideEligibilityData.eligibilityStartDate, application.coverageYear, isState.NJ]);

    useEffect(() => {
        bhpEligibilityValidate(overrideEligibilityData, setError);
    }, [bhpEligibilityValues]);

    const handleChangeTableData = (applicantId: number, key: string, value: string) => {
        const overridenData = overrideEligibilityData.applicantEligibility?.map((item) => {
            if (item.id !== applicantId) return item;

            let updatedItem = { ...item, [key]: value };

            if (key === APPLICANT_ELIGIBILITY_TYPE.NMPA && hasPremiumAssistanceSubsidyFields) {
                updatedItem = { ...updatedItem, stateSubsidyEligibility: value };
            }

            if (key === APPLICANT_ELIGIBILITY_TYPE.EXCHANGE_ELIGIBILITY && value === IS_ELIGIBLE.NO) {
                updatedItem = applyNotEligibleDefaults(updatedItem, stateCode);
            }

            if (key === APPLICANT_ELIGIBILITY_TYPE.EXCHANGE_ELIGIBILITY && value === IS_ELIGIBLE.YES) {
                const originalApplicant = data.applicantEligibility.find(a => a.id === applicantId);
                if (originalApplicant) {
                    updatedItem = restoreOriginalEligibility(updatedItem, originalApplicant, stateCode);
                }
            }

            return updatedItem;
        });

        let updatedEligibilityData = { ...overrideEligibilityData, applicantEligibility: overridenData };

        setOverrideEligibilityData(updatedEligibilityData);

        if (key === APPLICANT_ELIGIBILITY_TYPE.APTC_ELIGIBILITY || key === APPLICANT_ELIGIBILITY_TYPE.EXCHANGE_ELIGIBILITY) {
            aptcAmntValidate(overridenData, parseCurrencyToNumber(maximumAPTCValue), setError);
        }
    };

    const handleDateChange = (value: Date | string) => {
        setOverrideEligibilityData(prev => ({ ...prev, eligibilityStartDate: value || '' }));
    };

    const handleHouseholdSubsidyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        const prevValue = householdStateSubsidyAmountValue;
        if (newValue !== prevValue) {
            setIsHouseholdStateSubsidyAmountDirty(true);
        }
        setHouseholdStateSubsidyAmountValue(newValue || '$');
    };

    const handleHouseholdSubsidyBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const rawValue = e.target.value || householdStateSubsidyAmountValue;
        setHouseholdStateSubsidyAmountValue(formatCurrencyValue(rawValue));
    };

    const handleMaximumAPTCChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        if (newValue !== maximumAPTCValue) {
            setIsMaximumAPTCDirty(true);
        }
        setMaximumAPTCValue(newValue || '$');
    };

    const handleMaximumAPTCBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const rawValue = e.target.value || maximumAPTCValue;
        setMaximumAPTCValue(formatCurrencyValue(rawValue));
    };

    const onClose = () => {
        closeModal('csrOverrideProgramEligibilityModal');
    };

    const onSubmit = () => {
        let normalizedOverrideEligibilityData = overrideEligibilityData;
        if (isStateSubsidyOverrideEnabled && isHouseholdStateSubsidyAmountDirty) {
            normalizedOverrideEligibilityData = {
                ...normalizedOverrideEligibilityData,
                householdStateSubsidyAmount: parseCurrencyToNumber(householdStateSubsidyAmountValue),
            };
        }
        if (isFinancialAssistanceFlag && isMaximumAPTCDirty) {
            normalizedOverrideEligibilityData = {
                ...normalizedOverrideEligibilityData,
                maximumAPTC: parseCurrencyToNumber(maximumAPTCValue),
            };
        }

        if (isModalPhaseOverride) {
            setOverrideEligibilityData(normalizedOverrideEligibilityData);
            setOverrideProgramEligibilityModalPhase('confirmation');
        } else {
            submitOverrideProgElig(
                {
                    ...normalizedOverrideEligibilityData,
                    eligibilityStartDate: formatDate(
                        overrideEligibilityData.eligibilityStartDate,
                        MM_SLASH_DD_SLASH_YYYY,
                    ),
                },
                confirmationText,
                application.coverageYear,
                application.caseNumber,
            );
            onClose();
        }
    };

    const onBack = () => {
        setOverrideProgramEligibilityModalPhase('override');
    };

    return {
        overrideEligibilityData,
        householdStateSubsidyAmountValue,
        maximumAPTCValue,
        isHouseholdStateSubsidyAmountDirty,
        error,
        confirmationText,
        setConfirmationText,
        isModalPhaseOverride,
        isFinancialAssistanceFlag,
        isStateSubsidyOverrideEnabled,
        isBhpOverrideEnabled,
        hasPremiumAssistanceSubsidyFields,
        originalBhpTypes,
        handleChangeTableData,
        handleDateChange,
        handleHouseholdSubsidyChange,
        handleHouseholdSubsidyBlur,
        handleMaximumAPTCChange,
        handleMaximumAPTCBlur,
        onSubmit,
        onBack,
        onClose,
    };
};

export default useOverrideEligibility;
